import { createContext, useContext, type ReactNode } from 'react';
import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { TabNav } from './TabNav';

export interface TabbedPageTab {
  /** The tab's own address, absolute (`/admin/mail-log`). */
  path: string;
  label: string;
  icon?: ReactNode;
}

export interface TabbedPageProps {
  title: string;
  icon?: ReactNode;
  tabs: TabbedPageTab[];
  ariaLabel?: string;
}

const TabbedPageContext = createContext(false);

/**
 * True inside a `TabbedPage`: the page's own heading is the tab's label, so a
 * page that also stands alone drops its `<h1>` rather than saying it twice.
 */
export function useInTabbedPage(): boolean {
  return useContext(TabbedPageContext);
}

/**
 * One menu entry over several pages: a heading, a tab row, and the tab's page
 * below it. Each tab is a route of its own (rendered through `<Outlet>`), so
 * the address names the tab and a bookmark to any of the old pages still lands
 * on it.
 */
export function TabbedPage({ title, icon, tabs, ariaLabel }: TabbedPageProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The longest matching path wins, so `/communications` (the index tab) does
  // not also claim `/communications/inquiries`.
  const active =
    [...tabs]
      .sort((a, b) => b.path.length - a.path.length)
      .find((t) => matchPath({ path: t.path, end: false }, pathname))?.path ?? tabs[0]?.path;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      </div>
      <TabNav
        items={tabs.map((t) => ({ id: t.path, label: t.label, icon: t.icon }))}
        activeId={active ?? ''}
        onSelect={(path) => navigate(path)}
        ariaLabel={ariaLabel ?? title}
        className="border-b border-gray-200 mb-6"
      />
      <TabbedPageContext.Provider value={true}>
        <Outlet />
      </TabbedPageContext.Provider>
    </div>
  );
}
